import { Ref,useRef, useState } from 'react';

import { Box, Stack, TextField } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { IConstructDrawerContext } from 'common/components/DrawerContext/drawerContext';
import { EditDrawer } from 'common/components/EditDrawer';
import { DEFAULT_SUCCESS_MESSAGE } from 'common/components/EditDrawer/EditDrawer';
import {
    closeDrawer,
    selectCurrentOperation,
    selectNameInConflict,
    setNameInConflict,
    setSaveButtonEnabled
} from 'common/components/EditDrawer/editDrawerSlice';
import { DEFAULT_ERROR_HANDLER_INLINE_STYLES } from 'common/components/errorHandler/ErrorHandler';
import { selectPageInfo, setRows } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { EditDrawerContentProps, IEditingItemInfo,Operations } from 'common/types/TypeDefs';
import { handleEditDrawerError } from 'common/utils/errorHandlers';
import { editDrawerTextFieldStyles } from 'common/utils/StyleHelpers';
import { useSnackbar } from 'notistack';

import { postUserGroup, putUserGroup } from '../../common/services/api/UserGroups/UserGroups.thunk';

const constructEditContentComponent = (
    context: IConstructDrawerContext,
    tenantName: string,
    saveButtonFunction: () => void,
    nameFieldRef: Ref<HTMLInputElement | undefined>,
    descriptionFieldRef: Ref<HTMLInputElement | undefined>,
    editingItemInfo: IEditingItemInfo<{ name: string, description: string }>,
) => {
    const { editingRow } = editingItemInfo;

    return (
        <EditDrawer
            title={context.entityName}
            saveButtonFunction={saveButtonFunction}
            processing={context.processing}
        >
            <Stack spacing={2}>
                <TextField
                    sx={editDrawerTextFieldStyles(context.nameInConflict)}
                    inputRef={nameFieldRef}
                    defaultValue={editingRow?.name}
                    id="filled-basic"
                    size="small"
                    label="User Group Name"
                    placeholder="Type User Group Name"
                    variant="filled"
                    focused
                    autoFocus={true}
                    onChange={context.handleSaveBtnEnabled}
                    InputProps={{
                        disableUnderline: context.nameInConflict
                    }}
                />
                {context.nameInConflict &&
                  <Box component='div'
                      display='inline'
                      sx={DEFAULT_ERROR_HANDLER_INLINE_STYLES}>
                    User Group name already in use
                  </Box>
                }
                <TextField
                    inputRef={descriptionFieldRef}
                    defaultValue={editingRow?.description}
                    id="filled-basic"
                    size="small"
                    label="User Group Description"
                    placeholder="Type User Group Description"
                    variant="filled"
                    focused
                    sx={editDrawerTextFieldStyles(false)}
                />
                <TextField
                    sx={{
                        backgroundColor: 'background.paper',
                        borderRadius: '4px',
                    }}
                    size="small"
                    label="Tenant Name"
                    variant="filled"
                    defaultValue={tenantName}
                    disabled
                />
            </Stack>
        </EditDrawer>
    );
};

const constructCreateContentComponent = (
    context: IConstructDrawerContext,
    tenantName: string,
    saveButtonFunction: () => void,
    nameFieldRef: React.RefObject<HTMLInputElement | undefined>,
    descriptionFieldRef: React.RefObject<HTMLInputElement | undefined>,
) => (
    <EditDrawer
        title={context.entityName}
        saveButtonFunction={saveButtonFunction}
        processing={context.processing}
    >
        <Stack spacing={2}>
            <TextField
                inputRef={nameFieldRef}
                id="filled-basic"
                size="small"
                label="User Group Name"
                placeholder="Type User Group Name"
                variant="filled"
                focused
                autoFocus={true}
                onChange={context.handleSaveBtnEnabled}
                InputProps={{
                    disableUnderline: context.nameInConflict
                }}
                sx={editDrawerTextFieldStyles(context.nameInConflict)}
            />
            {context.nameInConflict &&
                <Box component='div'
                    display='inline'
                    sx={DEFAULT_ERROR_HANDLER_INLINE_STYLES}>
                    User Group name already in use
                </Box>
            }
            <TextField
                inputRef={descriptionFieldRef}
                id="filled-basic"
                size="small"
                label="User Group Description"
                placeholder="Type User Group Description"
                variant="filled"
                focused
                sx={editDrawerTextFieldStyles(false)}
            />
            <TextField
                sx={{
                    backgroundColor: 'background.paper',
                    borderRadius: '4px',
                }}
                size="small"
                label="Tenant Name"
                variant="filled"
                defaultValue={tenantName}
                disabled
            />
        </Stack>
    </EditDrawer>
);

export function UserGroupCreateContent({
    title,
    editingItemInfo,
}: EditDrawerContentProps<{ name: string, description: string }>) {
    const nameFieldRef = useRef<HTMLInputElement>(undefined);
    const descriptionFieldRef = useRef<HTMLInputElement>(undefined);
    const tenantDetails = useAppSelector(selectPageInfo);
    const { enqueueSnackbar } = useSnackbar();
    const action = useAppSelector(selectCurrentOperation);

    const [processing, setProcessing] = useState<boolean>(false);
    const nameInConflict = useAppSelector(selectNameInConflict);
    const handleSaveBtnEnabled = () => dispatch(setSaveButtonEnabled(!!nameFieldRef?.current?.value));

    const editSuccessHandler = (): void => {
        dispatch(setNameInConflict(false));
        dispatch(closeDrawer());
        enqueueSnackbar(DEFAULT_SUCCESS_MESSAGE, { variant: 'success' });
    };

    const dispatch = useAppDispatch();
    const userGroupPostCallback = () => {
        setProcessing(true);
        const userGroupOptions = {
            tenantId: tenantDetails?.id ?? '',
            name: nameFieldRef.current?.value ?? '',
            description: descriptionFieldRef.current?.value ?? '',
        };

        dispatch(postUserGroup(userGroupOptions))
            .unwrap()
            .then((result) => {
                enqueueSnackbar('The User Group was successfully created.', { variant: 'success' });
                dispatch(setRows(result.rows));
                dispatch(setNameInConflict(false));
                dispatch(closeDrawer());
                setProcessing(false);
            })
            .catch((error) => {
                handleEditDrawerError(Operations.Create, extractStatusCode(error.message), 'User Group', dispatch, enqueueSnackbar);
                setProcessing(false);
            });
    };

    const userGroupPutCallback = () => {
        if (!editingItemInfo) {
            return;
        }
        const userGroupOptions = {
            tenantId: tenantDetails?.id ?? '',
            name: nameFieldRef.current?.value ?? '',
            description: descriptionFieldRef.current?.value ?? '',
            id: editingItemInfo.editingItemId?.toString() ?? '',
        };

        setProcessing(true);
        dispatch(putUserGroup(userGroupOptions))
            .unwrap()
            .then(result => {
                editSuccessHandler();
                dispatch(setRows(result.rows));
                setProcessing(false);
            })
            .catch((error) => {
                handleEditDrawerError(Operations.Edit, extractStatusCode(error.message), 'User Group', dispatch, enqueueSnackbar);
                setProcessing(false);
            });
    };
    const context =  {
        entityName: title,
        processing,
        nameInConflict: nameInConflict ?? false,
        handleSaveBtnEnabled
    };
    switch(action) {
        case Operations.Create: {
            return constructCreateContentComponent(
                context,
                tenantDetails?.name ?? '',
                userGroupPostCallback,
                nameFieldRef,
                descriptionFieldRef,
            );
        }
        case Operations.Edit: {
            if (!editingItemInfo) {
                return null;
            }
            return constructEditContentComponent(
                context,
                tenantDetails?.name ?? '',
                userGroupPutCallback,
                nameFieldRef,
                descriptionFieldRef,
                editingItemInfo
            );
        }
        default: {
            return null;
        }
    }
}
